import { useMemo } from "react";

export interface MasteryRow {
  topicSlug: string;
  label: string;
  recentAccuracy: number | null;
  attemptCount: number;
}

export type ReadinessVerdict = "exam-ready" | "ready" | "almost" | "not-ready";

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

interface UseReadinessReturn {
  pct: number;
  verdict: ReadinessVerdict;
  verdictCopy: string;
  /** Actionable sub-copy. "N points to go. {weakest} is your biggest lever." */
  readySub: string;
  /** Top 6 by attempt count, then descending accuracy. */
  masteryTop: MasteryRow[];
}

// Derives the dashboard's readiness state from raw mastery rows.
// Replaces the previous inline computation in home.tsx — keeps the
// orchestrator focused on layout, this hook owns the math + copy.
export function useReadiness(
  mastery: MasteryRow[] | null,
): UseReadinessReturn {
  return useMemo(() => {
    if (!mastery) {
      return {
        pct: 0,
        verdict: "not-ready",
        verdictCopy: "Just getting started",
        readySub: "Start practicing to see your diagnostic.",
        masteryTop: [],
      };
    }
    const seen = mastery.filter((r) => r.attemptCount > 0);
    const totalAnswered = seen.reduce((s, t) => s + t.attemptCount, 0);
    const totalCorrect = seen.reduce(
      (s, t) => s + (t.recentAccuracy ?? 0) * t.attemptCount,
      0,
    );
    const pct =
      totalAnswered > 0 ? clampPct((totalCorrect / totalAnswered) * 100) : 0;
    const verdict: ReadinessVerdict =
      pct >= 90
        ? "exam-ready"
        : pct >= 80
          ? "ready"
          : pct >= 60
            ? "almost"
            : "not-ready";
    const verdictCopy =
      verdict === "exam-ready"
        ? "Exam-ready"
        : verdict === "ready"
          ? "Close to ready"
          : verdict === "almost"
            ? "Almost ready"
            : "Just getting started";

    // Actionable sub-copy ("6 points to go. Air Brakes is your biggest
    // lever right now."). Weakest topic = lowest accuracy with attempts.
    const weakest = seen.sort(
      (a, b) => (a.recentAccuracy ?? 0) - (b.recentAccuracy ?? 0),
    )[0];
    const pointsToReady = Math.max(0, 80 - pct);
    const readySub =
      !weakest || pct === 0
        ? "Start practicing to see your diagnostic."
        : verdict === "exam-ready"
          ? "You're consistently above the pass line — book it."
          : pointsToReady === 0
            ? `Sustain it — and run the mock exam when you're confident.`
            : `${pointsToReady} ${pointsToReady === 1 ? "point" : "points"} to go. ${weakest.label} is your biggest lever right now.`;

    // Top 6 by attempt count, then descending accuracy — surfaces
    // both depth and weak areas.
    const masteryTop = [...seen]
      .sort(
        (a, b) =>
          b.attemptCount - a.attemptCount ||
          (a.recentAccuracy ?? 0) - (b.recentAccuracy ?? 0),
      )
      .slice(0, 6);

    return { pct, verdict, verdictCopy, readySub, masteryTop };
  }, [mastery]);
}
